import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    useDeepCompareEffect,
    useIsomorphicLayoutEffect,
    useLatest,
} from 'ahooks';
import type { Swiper as SwiperCore } from 'swiper';
import {
    Button,
    Carousel,
    pxToRem,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import {
    ArrowLeftSlim,
    ArrowRightSlim,
    DownloadLine,
    PlayFill,
    SearchLine,
} from '@archipro-design/icons';
import { useTracker } from '@archipro-website/tracker';
import ImageSlide from './ImageSlide';
import VideoSlide from './VideoSlide';
import * as S from './ProductGallery.style';
import {
    PRODUCT_GALLERY_SIZE,
    PRODUCT_THUMB_SIZE,
} from './ProductGallery.style';
import PhotoViewerModal from './PhotoViewerModal';
import type {
    LibraryDiagram,
    LibraryImage,
    LibraryVideo,
} from '@modules/product/type/product-gallery-types';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import useGalleryVariantSelect from '@modules/product/hook/use-gallery-variant-select';
import getGallerySlides from '~/modules/product/util/get-gallery-slides';
import ProductGalleryThumbs from './ProductGalleryThumbs';
import { usePermissions } from '@modules/user';
import { PermissionEnum } from 'generated/graphql';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useFavouriteVariant } from '../../hook/use-favourite-variant';
import type { AwardProps } from '@modules/page-builder/component/detail/banner-awards/types';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { useFeature } from '@modules/root';
import { encodeDownloadLink } from '~/modules/page-builder/component/detail/detail-single-image-shell/HoverButtonsImage';
import { hex2rgba } from '~/utils/colorHelper';

interface ProductGalleryData {
    ID: number;
    LibraryImages: LibraryImage[];
    LibraryVideos: LibraryVideo[];
    DiagramImages: LibraryDiagram[];
    Awards?: AwardProps[];
}

interface ProductGalleryProps {
    data: ProductGalleryData;
    title?: string;
    loop?: boolean;
}

const ProductGallery = ({ data, title, loop }: ProductGalleryProps) => {
    const { ID, LibraryImages, LibraryVideos, DiagramImages } = data;
    const [gallery, setGallery] = useState<SwiperCore | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const tracker = useTracker();
    const enableAwardBadges = useFeature('award_badges');

    const { css } = useStyles({ swiperInitialised: !!gallery });

    const hasMarketingView = usePermissions(PermissionEnum.BmMarketingView);

    const stack = useMemo(
        () => getGallerySlides({ LibraryImages, LibraryVideos, DiagramImages }),
        [LibraryImages, LibraryVideos, DiagramImages]
    );

    const { slideIndex } = useGalleryVariantSelect(stack);
    const defaultIndex = useLatest(slideIndex);

    const slideToIndex = useCallback(
        (index: number, speed: number = 400) => {
            loop
                ? gallery?.slideToLoop(index, speed, false)
                : gallery?.slideTo(index, speed, false);
        },
        [loop, gallery]
    );
    const { siteVariables } = useTheme();
    const [activeIndex, setActiveIndex] = useState(slideIndex);
    const favouriteItem = useFavouriteVariant(data.ID);

    const slides = stack.map((slide) => {
        const isVideo = isLibraryVideo(slide);
        const imageThumbnailSrc = !isVideo
            ? slide.__typename === 'DiagramImage'
                ? slide.Thumbnail.Desktop
                : slide.LibraryImageFile?.ThumbnailBM180
            : slide.LibraryImage?.LibraryImageFile?.VideoCover.Desktop ||
              slide.ThumbnailOfficial;
        const isMultiplied =
            slide.__typename === 'LibraryImage' ? slide.IsMultiplied : false;
        const downloadLink = encodeDownloadLink(
            !isVideo && hasMarketingView
                ? (stack[activeIndex] as LibraryImage)?.DownloadLink || ''
                : ''
        );
        return {
            key: `${slide.ID}-ImageSlide`,
            id: slide.ID,
            content: (
                <>
                    <div
                        key="favourite-button"
                        className="absolute right-4 top-4 z-[1000] flex items-center gap-2"
                    >
                        <SaveToDesignBoard
                            {...favouriteItem}
                            heartButton={{
                                className: css(S.FavouriteButton),
                                size: 18,
                                color: 'primary',
                                variant: 'text',
                                variables: {
                                    buttonRadius: '50%',
                                    buttonPadding: pxToRem(0),
                                    primaryNoBKGButtonTextColorNormal:
                                        siteVariables.colors.primitive.white,
                                    primaryNoBKGButtonTextColorHover:
                                        siteVariables.colors.primitive.white,
                                    primaryNoBKGButtonBackgroundColorNormal:
                                        hex2rgba(
                                            siteVariables.colors.primitive
                                                .black,
                                            0.1
                                        ),
                                    primaryNoBKGButtonBackgroundColorHover:
                                        hex2rgba(
                                            siteVariables.colors.primitive
                                                .black,
                                            0.4
                                        ),
                                },
                            }}
                        />
                        <Button
                            color="primary"
                            variant="text"
                            icon={<DownloadLine />}
                            as="a"
                            href={downloadLink}
                            target={'_blank'}
                            rel="noopener"
                            download
                            size={16}
                            className={css(S.DownloadButton)}
                            aria-hidden={isVideo || !downloadLink}
                            style={{
                                borderRadius: '50%',
                            }}
                        />
                    </div>
                    {!isVideo ? (
                        <ImageSlide
                            libraryImage={
                                slide.LibraryImageFile?.DetailPageSquare
                                    .Desktop ?? ''
                            }
                            isMultiplied={isMultiplied}
                            onClick={() => {
                                tracker.log('PhotoViewerTrigger', {
                                    url: new URL(window.location.href),
                                    data: {
                                        LibraryItemID: ID || 0,
                                    },
                                });
                                setModalOpen(true);
                            }}
                            height={PRODUCT_GALLERY_SIZE}
                            width={PRODUCT_GALLERY_SIZE}
                            sizes={getImagesSizes({
                                desktop: PRODUCT_GALLERY_SIZE,
                                mobile: 375,
                            })}
                            loadingValue={'eager'}
                            alt={
                                slide.AltText || `${title} gallery detail image`
                            }
                        />
                    ) : (
                        <VideoSlide libraryVideo={slide} />
                    )}
                </>
            ),
            thumbnail: (
                <ImageSlide
                    libraryImage={imageThumbnailSrc ?? ''}
                    isMultiplied={isMultiplied}
                    width={PRODUCT_THUMB_SIZE}
                    height={PRODUCT_THUMB_SIZE}
                    loadingValue={'eager'}
                    alt={
                        (slide.__typename !== 'LibraryVideo' &&
                            slide.AltText) ||
                        `${title} gallery detail image`
                    }
                    sizes={getImagesSizes(PRODUCT_THUMB_SIZE)}
                    isVideo={isVideo}
                />
            ),
        };
    });

    const isVideo = isLibraryVideo(stack[activeIndex]);

    useEffect(() => {
        slideToIndex(slideIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slideIndex]);

    useDeepCompareEffect(() => {
        const activeIndex = defaultIndex.current ?? 0;
        setActiveIndex(activeIndex);
        slideToIndex(activeIndex, 0);
    }, [data, defaultIndex]);

    useIsomorphicLayoutEffect(() => {
        const current = gallery;

        if (current) {
            const onSliceChange = (swiper: SwiperCore) => {
                setActiveIndex(swiper.realIndex);
            };
            current.on('slideChange', onSliceChange);
            return () => {
                current.off('slideChange', onSliceChange);
            };
        }
    }, [gallery]);

    return (
        <div className={css(S.ProductGallerySticky)}>
            <div className={css(S.ProductGalleryContainer)}>
                <Carousel
                    items={slides}
                    getSwiperApi={(swiper) => setGallery(swiper as SwiperCore)}
                    mixBlendMode="difference"
                    topCarouselActions={[
                        isVideo ? (
                            <Button
                                color="primary"
                                variant="outlined"
                                key="video-player-button-overlay"
                                size={44}
                                className={css(S.VideoButton)}
                                icon={<PlayFill />}
                                design={{ position: 'absolute' }}
                            />
                        ) : (
                            <div
                                className={css(S.ViewImageOverlay)}
                                key="view-photo-overlay"
                                onClick={() => {
                                    tracker.log('PhotoViewerTrigger', {
                                        url: new URL(window.location.href),
                                        data: {
                                            LibraryItemID: ID || 0,
                                        },
                                    });
                                    setModalOpen(true);
                                }}
                            >
                                <Button
                                    color="primary"
                                    variant="text"
                                    icon={<SearchLine />}
                                    variables={{
                                        buttonRadius: '0',
                                        primaryNoBKGButtonBackgroundColorHover:
                                            'transparent',
                                    }}
                                    design={{ position: 'absolute' }}
                                />
                            </div>
                        ),
                    ]}
                    paddleNext={{
                        children: (
                            <ArrowRightSlim className={css(S.PaddleButton)} />
                        ),
                        variables: {
                            paddleHeight: pxToRem(160),
                            paddleBackgroundColor: 'rgba(242, 241, 240, 0.5)',
                        },
                    }}
                    paddlePrevious={{
                        children: (
                            <ArrowLeftSlim className={css(S.PaddleButton)} />
                        ),
                        variables: {
                            paddleHeight: pxToRem(160),
                            paddleBackgroundColor: 'rgba(242, 241, 240, 0.5)',
                        },
                    }}
                    className={css(S.Carousel)}
                    variables={{
                        paddleHeight: pxToRem(110),
                        paddleBackgroundColor: 'rgba(242, 241, 240, 0.5)',
                        containerWidth: pxToRem(PRODUCT_GALLERY_SIZE),
                        containerHeight: pxToRem(PRODUCT_GALLERY_SIZE),
                    }}
                    activeIndex={activeIndex}
                    defaultActiveIndex={activeIndex}
                    loop={loop}
                    hidePaddles={slides.length < 2}
                >
                    {enableAwardBadges &&
                        data.Awards &&
                        data.Awards.length > 0 && (
                            <BannerAwards
                                awards={data.Awards}
                                awardsPerSlide={5}
                            />
                        )}
                </Carousel>
                <ProductGalleryThumbs
                    navigation={slides}
                    activeGallerySlide={gallery ? activeIndex : 0}
                    swipeInitialised={!!gallery}
                    slideToIndex={slideToIndex}
                />
            </div>
            {modalOpen && (
                <PhotoViewerModal
                    stack={stack}
                    selectedSlide={activeIndex}
                    open={modalOpen}
                    toggle={() => setModalOpen((curr) => !curr)}
                    itemID={ID}
                    title={title}
                />
            )}
        </div>
    );
};

export default ProductGallery;
